import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Mail, CheckCircle2, XCircle, Clock } from 'lucide-react';
import api from '@/lib/api';

const TRIGGER_LABELS = {
  WELCOME: 'Welcome',
  INVOICE_FINALIZED: 'Invoice Finalized',
  PAYMENT_RECORDED: 'Payment Recorded',
  QUESTIONNAIRE_ASSIGNED: 'Questionnaire Assigned',
  SESSION_CANCELLED: 'Session Cancelled',
  SESSION_RESCHEDULED: 'Session Rescheduled',
  SESSION_REMINDER_24H: 'Reminder (24h)',
  SESSION_REMINDER_1H: 'Reminder (1h)',
  THERAPIST_DAILY_SCHEDULE: 'Daily Schedule',
  CLIENT_TRANSFERRED: 'Client Transferred',
  PASSWORD_RESET: 'Password Reset',
  INVOICE_OVERDUE: 'Invoice Overdue',
  QUESTIONNAIRE_DEADLINE: 'Assessment Deadline',
};

export default function EmailLogsPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ SENT: 0, FAILED: 0, PENDING: 0 });
  const [filters, setFilters] = useState({ status: 'ALL', trigger_type: 'ALL', search: '' });
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'ALL') params.append('status', filters.status);
      if (filters.trigger_type !== 'ALL') params.append('trigger_type', filters.trigger_type);
      if (filters.search) params.append('search', filters.search);
      const [logsRes, statsRes] = await Promise.all([
        api.get(`/email-logs?${params.toString()}`),
        api.get('/email-logs/stats'),
      ]);
      setItems(logsRes.data.items || []);
      setStats(statsRes.data || {});
    } catch (e) { /* admin guard handled by backend */ }
    setLoading(false);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filters.status, filters.trigger_type]);

  const runJob = async (jobId) => {
    if (!window.confirm(`Run "${jobId}" now? This will send emails for any matching pending items.`)) return;
    try {
      await api.post(`/email-logs/run-job/${jobId}`);
      await fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Job failed'); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  const StatusPill = ({ status }) => {
    const map = {
      SENT: { bg: 'bg-[#0D8B7D]/10', tx: 'text-[#0D8B7D]', Icon: CheckCircle2 },
      FAILED: { bg: 'bg-[#D3544E]/10', tx: 'text-[#D3544E]', Icon: XCircle },
      PENDING: { bg: 'bg-[#B8860B]/10', tx: 'text-[#B8860B]', Icon: Clock },
    };
    const m = map[status] || map.PENDING;
    return (
      <span className={`inline-flex items-center gap-1 ${m.bg} ${m.tx} px-2 py-0.5 rounded-full text-[11px] font-medium`}>
        <m.Icon className="w-3 h-3" />
        {status || 'PENDING'}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="email-logs-page">
        <div>
          <h1 className="text-3xl font-['Outfit'] font-semibold text-[#0D8B7D] flex items-center gap-2"><Mail className="w-7 h-7" /> Email Logs</h1>
          <p className="text-[#4A6B68] text-sm mt-1">Every transactional email the system sent (or attempted) — with delivery status.</p>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Sent', value: stats.SENT || 0, color: 'text-[#0D8B7D]', bg: 'bg-[#E8F5F3]' },
            { label: 'Failed', value: stats.FAILED || 0, color: 'text-[#D3544E]', bg: 'bg-[#FEF2F2]' },
            { label: 'Pending', value: stats.PENDING || 0, color: 'text-[#B8860B]', bg: 'bg-[#FEF6E7]' },
          ].map(s => (
            <Card key={s.label} className={`${s.bg} p-5 rounded-2xl border-0`} data-testid={`email-stat-${s.label.toLowerCase()}`}>
              <p className="text-xs text-[#4A6B68] uppercase tracking-wider">{s.label}</p>
              <p className={`text-3xl font-bold ${s.color} mt-1`}>{s.value}</p>
            </Card>
          ))}
        </div>

        {/* Manual job runners */}
        <Card className="p-4 rounded-2xl border-[#E5F2F1] bg-white">
          <p className="text-xs uppercase tracking-wider text-[#4A6B68] font-medium mb-3">Scheduled Jobs · Run Manually</p>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'reminders_24h', label: '24-Hour Reminders' },
              { id: 'reminders_1h', label: '1-Hour Reminders' },
              { id: 'daily_schedule', label: "Therapist Daily Schedule" },
              { id: 'questionnaire_deadline', label: 'Assessment Deadline' },
            ].map(j => (
              <button key={j.id} onClick={() => runJob(j.id)}
                className="px-3 py-1.5 rounded-lg bg-[#E8F5F3] text-[#075E55] text-xs font-medium hover:bg-[#0D8B7D] hover:text-white transition"
                data-testid={`run-job-${j.id}`}>{j.label}</button>
            ))}
          </div>
          <p className="text-[11px] text-[#4A6B68] mt-2">These jobs run automatically (24h: hourly · 1h: every 5 min · Daily schedule: 07:00 IST · Deadline: 09:00 IST). Use these buttons only for ad-hoc dispatch.</p>
        </Card>

        {/* Filters */}
        <Card className="p-4 rounded-2xl border-[#E5F2F1] bg-white">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input placeholder="Search recipient or subject..."
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') fetchData(); }}
              className="rounded-xl border-[#E5F2F1]"
              data-testid="email-search" />
            <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="email-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Status</SelectItem>
                <SelectItem value="SENT">Sent</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.trigger_type} onValueChange={v => setFilters({ ...filters, trigger_type: v })}>
              <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="email-trigger-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Triggers</SelectItem>
                {Object.entries(TRIGGER_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={fetchData} className="px-4 py-2 rounded-xl bg-[#0D8B7D] text-white text-sm font-medium hover:bg-[#0B786C]" data-testid="email-refresh-btn">Refresh</button>
          </div>
        </Card>

        {/* Table */}
        <Card className="rounded-2xl border-[#E5F2F1] bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4FAF9]">
                <TableHead className="text-[#075E55]">Date</TableHead>
                <TableHead className="text-[#075E55]">Recipient</TableHead>
                <TableHead className="text-[#075E55]">Subject</TableHead>
                <TableHead className="text-[#075E55]">Trigger</TableHead>
                <TableHead className="text-[#075E55]">Status</TableHead>
                <TableHead className="text-[#075E55]">Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-6 text-[#4A6B68]">Loading…</TableCell></TableRow>}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#4A6B68]">No emails logged yet.</TableCell></TableRow>
              )}
              {items.map((row, idx) => (
                <TableRow key={idx} data-testid={`email-row-${idx}`}>
                  <TableCell className="text-xs whitespace-nowrap">{fmtDate(row.sent_at)}</TableCell>
                  <TableCell className="text-xs">{row.recipient_email}</TableCell>
                  <TableCell className="text-xs max-w-xs truncate" title={row.subject}>{row.subject}</TableCell>
                  <TableCell className="text-xs">{TRIGGER_LABELS[row.trigger_type] || row.trigger_type}</TableCell>
                  <TableCell><StatusPill status={row.status} /></TableCell>
                  <TableCell className="text-xs text-[#D3544E] max-w-xs truncate" title={row.error_message}>{row.error_message || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </Layout>
  );
}
